import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { useI18n } from '@/i18n';
import { useSecretVersions } from '../hooks/use-secrets';
import type { Secret } from '../types';

interface SecretVersionHistoryModalProps {
  open: boolean;
  secret: Secret | null;
  onClose: () => void;
}

export function SecretVersionHistoryModal({
  open,
  secret,
  onClose,
}: SecretVersionHistoryModalProps) {
  const { t } = useI18n();
  const versionsQuery = useSecretVersions(open ? secret?.id ?? null : null);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('admin.secrets.history.title') || 'Version history'}
          </DialogTitle>
          <DialogDescription>
            {secret
              ? t('admin.secrets.history.description', { name: secret.name }) ||
                `Immutable snapshots for ${secret.name}. Only the owner can see this history.`
              : t('admin.secrets.history.descriptionFallback') ||
                'Immutable snapshots. Only the owner can see this history.'}
          </DialogDescription>
        </DialogHeader>

        {versionsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : versionsQuery.data && versionsQuery.data.total > 0 ? (
          <div className="space-y-3">
            {versionsQuery.data.data.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">v{item.version}</Badge>
                    {secret?.version === item.version ? (
                      <span className="text-xs text-muted-foreground">
                        {t('admin.secrets.history.current') || 'Current'}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.secrets.history.createdBy') || 'Created by'}: {item.created_by}
                  </p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(item.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('admin.secrets.history.empty') || 'No versions available.'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}